import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createTenantSchema, type CreateTenantInput, type TenantResponse } from '@cumpli/shared';
import { Building2 } from 'lucide-react';
import { api } from '../lib/api';
import { useTenants, formatDate } from '../lib/hooks';
import { useToast } from '../lib/use-toast';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  FieldError,
  Input,
  Label,
  PageHeader,
  Pill,
  Skeleton,
  type Tone,
} from '../components/ui';

type TenantStatus = 'active' | 'suspended' | 'offboarding';

const STATUS_TONE: Record<TenantStatus, Tone> = {
  active: 'green',
  suspended: 'red',
  offboarding: 'amber',
};

const STATUS_KEY: Record<TenantStatus, string> = {
  active: 'tenants.statusActive',
  suspended: 'tenants.statusSuspended',
  offboarding: 'tenants.statusOffboarding',
};

const PLAN_KEY: Record<string, string> = {
  standard: 'tenants.planStandard',
  premium: 'tenants.planPremium',
};

const STATUS_ACTION_TITLE: Record<TenantStatus, string> = {
  active: 'tenants.reactivate',
  suspended: 'tenants.suspend',
  offboarding: 'tenants.offboard',
};

const STATUS_ACTION_CONFIRM: Record<TenantStatus, string> = {
  active: 'tenants.reactivateConfirm',
  suspended: 'tenants.suspendConfirm',
  offboarding: 'tenants.offboardConfirm',
};

/** super_admin only (§9) — provision a tenant + its first Tenant Admin. */
export default function AdminTenants() {
  const { t } = useTranslation();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: tenants, isLoading, isError } = useTenants();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateTenantInput>({
    resolver: zodResolver(createTenantSchema),
    defaultValues: { planTier: 'standard' },
  });

  const create = useMutation({
    mutationFn: api.createTenant,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast({ message: t('tenants.created'), kind: 'success' });
      reset({ legalName: '', planTier: 'standard', admin: { email: '', fullName: '' } });
    },
    onError: () => toast({ message: t('common.genericError'), kind: 'error' }),
  });

  const [pending, setPending] = useState<{ tenant: TenantResponse; status: TenantStatus } | null>(
    null
  );
  const [planFor, setPlanFor] = useState<TenantResponse | null>(null);
  const [planTier, setPlanTier] = useState<'standard' | 'premium'>('standard');

  const update = useMutation({
    mutationFn: (v: { id: string; body: { status?: TenantStatus; planTier?: 'standard' | 'premium' } }) =>
      api.updateTenant(v.id, v.body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tenants'] });
      toast({ message: t('tenants.updated'), kind: 'success' });
      setPending(null);
      setPlanFor(null);
    },
    onError: () => toast({ message: t('common.genericError'), kind: 'error' }),
  });

  const openPlanDialog = (tn: TenantResponse) => {
    setPlanFor(tn);
    setPlanTier(tn.planTier === 'standard' || tn.planTier === 'premium' ? tn.planTier : 'standard');
  };

  const renderLifecycleActions = (tn: TenantResponse) => (
    <>
      {tn.status === 'active' && (
        <>
          <Button
            variant="ghost"
            className="px-2 py-1 text-xs"
            onClick={() => setPending({ tenant: tn, status: 'suspended' })}
          >
            {t('tenants.suspend')}
          </Button>
          <Button
            variant="ghost"
            className="px-2 py-1 text-xs"
            onClick={() => setPending({ tenant: tn, status: 'offboarding' })}
          >
            {t('tenants.offboard')}
          </Button>
        </>
      )}
      {tn.status === 'suspended' && (
        <>
          <Button
            variant="ghost"
            className="px-2 py-1 text-xs"
            onClick={() => setPending({ tenant: tn, status: 'active' })}
          >
            {t('tenants.reactivate')}
          </Button>
          <Button
            variant="ghost"
            className="px-2 py-1 text-xs"
            onClick={() => setPending({ tenant: tn, status: 'offboarding' })}
          >
            {t('tenants.offboard')}
          </Button>
        </>
      )}
      {tn.status === 'offboarding' && (
        <Button
          variant="ghost"
          className="px-2 py-1 text-xs"
          onClick={() => setPending({ tenant: tn, status: 'active' })}
        >
          {t('tenants.reactivate')}
        </Button>
      )}
      <Button variant="ghost" className="px-2 py-1 text-xs" onClick={() => openPlanDialog(tn)}>
        {t('tenants.changePlan')}
      </Button>
    </>
  );

  return (
    <div className="space-y-6">
      <PageHeader title={t('tenants.title')} />

      <Card className="max-w-lg">
        <h2 className="mb-4 font-serif text-lg font-medium">{t('tenants.provision')}</h2>
        <form onSubmit={handleSubmit((d) => create.mutate(d))} className="space-y-4">
          <div>
            <Label htmlFor="legalName">{t('tenants.legalName')} *</Label>
            <Input id="legalName" {...register('legalName')} />
            <FieldError message={errors.legalName && t('common.requiredField')} />
          </div>
          <div>
            <Label htmlFor="planTier">{t('tenants.plan')}</Label>
            <select
              id="planTier"
              {...register('planTier')}
              className="h-10 w-full rounded-md border border-line-strong bg-panel px-3 text-sm"
            >
              <option value="standard">{t('tenants.planStandard')}</option>
              <option value="premium">{t('tenants.planPremium')}</option>
            </select>
          </div>
          <fieldset className="space-y-4 rounded-md border border-line p-4">
            <legend className="px-1 text-sm font-medium text-ink-soft">
              {t('tenants.adminSection')}
            </legend>
            <div>
              <Label htmlFor="adminEmail">{t('tenants.adminEmail')} *</Label>
              <Input id="adminEmail" type="email" {...register('admin.email')} />
              <FieldError message={errors.admin?.email && t('users.invalidEmail')} />
            </div>
            <div>
              <Label htmlFor="adminName">{t('tenants.adminName')} *</Label>
              <Input id="adminName" {...register('admin.fullName')} />
              <FieldError message={errors.admin?.fullName && t('users.nameLength')} />
            </div>
          </fieldset>
          <Button type="submit" disabled={isSubmitting || create.isPending}>
            {t('common.save')}
          </Button>
        </form>
      </Card>

      {/* mobile: stacked cards (no horizontal scroll) */}
      <div data-testid="tenant-cards" className="grid gap-2.5 sm:hidden">
        {isLoading &&
          Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full rounded-2xl" />
          ))}
        {!isLoading &&
          !isError &&
          tenants?.map((tn) => (
            <div key={tn.id} className="rounded-2xl border border-line bg-panel p-4 shadow-card">
              <div className="font-bold text-ink">{tn.legalName}</div>
              <div className="mt-1.5 flex flex-wrap items-center gap-2">
                <Pill tone={STATUS_TONE[tn.status]}>
                  {t(STATUS_KEY[tn.status] ?? 'tenants.statusActive')}
                </Pill>
                <Pill tone="slate">{t(PLAN_KEY[tn.planTier] ?? 'tenants.planStandard')}</Pill>
              </div>
              <div className="mt-2 text-sm text-ink-soft">
                {t('tenants.userCount')}: <span className="tabular-nums">{tn.userCount ?? '—'}</span>
              </div>
              <div className="mt-1 text-sm text-ink-soft">
                {t('tenants.createdAt')}: {formatDate(tn.createdAt, '—')}
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5 border-t border-line pt-3">
                {renderLifecycleActions(tn)}
              </div>
            </div>
          ))}
      </div>

      {/* desktop: table */}
      <Card className="hidden overflow-hidden p-0 sm:block">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-sm">
            <thead className="border-b border-line bg-canvas text-left">
              <tr>
                <th scope="col" className="px-4 py-3 font-medium text-ink-soft">
                  {t('tenants.legalName')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium text-ink-soft">
                  {t('tenants.status')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium text-ink-soft">
                  {t('tenants.plan')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium text-ink-soft">
                  {t('tenants.userCount')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium text-ink-soft">
                  {t('tenants.createdAt')}
                </th>
                <th scope="col" className="px-4 py-3 font-medium text-ink-soft">
                  {t('common.actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading &&
                Array.from({ length: 3 }).map((_, i) => (
                  <tr key={i} className="border-t border-line">
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-40" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-8" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-24" />
                    </td>
                    <td className="px-4 py-3">
                      <Skeleton className="h-4 w-24" />
                    </td>
                  </tr>
                ))}
              {!isLoading &&
                tenants?.map((tn) => (
                  <tr key={tn.id} className="border-t border-line transition-colors hover:bg-canvas/60">
                    <td className="px-4 py-3 font-medium text-ink">{tn.legalName}</td>
                    <td className="px-4 py-3">
                      <Pill tone={STATUS_TONE[tn.status]}>
                        {t(STATUS_KEY[tn.status] ?? 'tenants.statusActive')}
                      </Pill>
                    </td>
                    <td className="px-4 py-3">
                      <Pill tone="slate">
                        {t(PLAN_KEY[tn.planTier] ?? 'tenants.planStandard')}
                      </Pill>
                    </td>
                    <td className="px-4 py-3 tabular-nums text-ink-soft">
                      {tn.userCount ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-ink-soft">{formatDate(tn.createdAt, '—')}</td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <div className="flex flex-wrap gap-1.5">{renderLifecycleActions(tn)}</div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Card>

      {!isLoading && !isError && !tenants?.length && (
        <Card>
          <EmptyState
            icon={<Building2 className="h-8 w-8" />}
            title={t('tenants.emptyTitle')}
            hint={t('tenants.emptyHint')}
          />
        </Card>
      )}

      {isError && (
        <Card>
          <p className="text-center text-sm text-ink-soft">{t('common.genericError')}</p>
        </Card>
      )}

      <Dialog
        open={pending !== null}
        title={pending ? t(STATUS_ACTION_TITLE[pending.status]) : ''}
        onClose={() => setPending(null)}
      >
        {pending && (
          <>
            <p className="mb-4 text-sm text-ink-soft">
              {t(STATUS_ACTION_CONFIRM[pending.status], { name: pending.tenant.legalName })}
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPending(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant={pending.status === 'active' ? 'default' : 'destructive'}
                disabled={update.isPending}
                onClick={() => update.mutate({ id: pending.tenant.id, body: { status: pending.status } })}
              >
                {t('common.confirm')}
              </Button>
            </div>
          </>
        )}
      </Dialog>

      <Dialog open={planFor !== null} title={t('tenants.changePlan')} onClose={() => setPlanFor(null)}>
        {planFor && (
          <>
            <div className="mb-4">
              <Label htmlFor="planTierEdit">{t('tenants.plan')}</Label>
              <select
                id="planTierEdit"
                value={planTier}
                onChange={(e) => setPlanTier(e.target.value as 'standard' | 'premium')}
                className="h-10 w-full rounded-md border border-line-strong bg-panel px-3 text-sm"
              >
                <option value="standard">{t('tenants.planStandard')}</option>
                <option value="premium">{t('tenants.planPremium')}</option>
              </select>
            </div>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setPlanFor(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                disabled={update.isPending}
                onClick={() => update.mutate({ id: planFor.id, body: { planTier } })}
              >
                {t('common.save')}
              </Button>
            </div>
          </>
        )}
      </Dialog>
    </div>
  );
}
